import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme, LinearProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import logoImage from '../assets/FullLogo-removebg-preview.png';
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-10px) rotate(5deg);
  }
  100% {
    transform: translateY(0px) rotate(0deg);
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const rotate = keyframes`
  0% {
    transform: translate(-50%, -50%) rotateZ(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotateZ(360deg);
  }
`;

const rotateccw = keyframes`
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(-360deg);
  }
`;

const spin = keyframes`
  0%,
  100% {
    box-shadow: .2em 0px 0 0px currentcolor;
  }
  12% {
    box-shadow: .2em .2em 0 0 currentcolor;
  }
  25% {
    box-shadow: 0 .2em 0 0px currentcolor;
  }
  37% {
    box-shadow: -.2em .2em 0 0 currentcolor;
  }
  50% {
    box-shadow: -.2em 0 0 0 currentcolor;
  }
  62% {
    box-shadow: -.2em -.2em 0 0 currentcolor;
  }
  75% {
    box-shadow: 0px -.2em 0 0 currentcolor;
  }
  87% {
    box-shadow: .2em -.2em 0 0 currentcolor;
  }
`;

const SplashContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: `
    linear-gradient(135deg, 
      ${theme.palette.primary.dark} 0%,
      ${theme.palette.primary.main} 50%,
      ${theme.palette.primary.light} 100%),
    linear-gradient(45deg,
      ${theme.palette.secondary.dark}40 0%,
      ${theme.palette.secondary.main}40 100%)
  `,
  backgroundSize: '400% 400%, 200% 200%',
  backgroundBlendMode: 'overlay',
  animation: `${gradientAnimation} 15s ease infinite`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.background.default,
    pointerEvents: 'none',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: '3rem',
  padding: '2rem',
  borderRadius: '20px',
  background: 'transparent',
  // backdropFilter: 'blur(10px)',
 
  animation: `${fadeIn} 1s ease-out, ${pulse} 3s ease-in-out infinite`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // border: `2px solid ${theme.palette.primary.main}1A`,
    borderRadius: '20px',
    opacity: 0.5,
  },
}));

const CustomLoader = styled(Box)(({ theme }) => ({
  transform: 'rotateZ(45deg)',
  perspective: '1000px',
  borderRadius: '50%',
  width: '48px',
  height: '48px',
  color: theme.palette.text.secondary,
  position: 'relative',
  '&::before, &::after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
    width: 'inherit',
    height: 'inherit',
    borderRadius: '50%',
    transform: 'rotateX(70deg)',
    animation: `${spin} 1s linear infinite`,
  },
  '&::after': {
    color: theme.palette.secondary.main,
    transform: 'rotateY(70deg)',
    animationDelay: '.4s',
  },
}));

const LoadingText = styled(Typography)(({ theme }) => ({
  animation: `${fadeIn} 1s ease-out`,
  textAlign: 'center',
  letterSpacing: '0.5px',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  maxWidth: '80%',
  margin: '0 auto',
  fontSize: '0.8rem',
  position: 'absolute',
  bottom: '10px',
  color: theme.palette.secondary.main,
  // left: '50%',
  // transform: 'translateX(-50%)',
  textTransform: 'uppercase',
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  width: '200px',
  marginBottom: '1rem',
  '& .MuiLinearProgress-root': {
    height: '6px',
    borderRadius: '3px',
    backgroundColor: `${theme.palette.text.secondary}20`,
  },
  '& .MuiLinearProgress-bar': {
    borderRadius: '3px',
    background: `linear-gradient(90deg, 
      ${theme.palette.text.secondary}, 
      ${theme.palette.secondary.main}
    )`,
    boxShadow: `0 0 10px ${theme.palette.secondary.main}40`,
  },
}));

const ProgressText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  fontWeight: 500,
  textAlign: 'center',
  marginTop: '0.5rem',
  opacity: 0.8,
}));

const SplashScreen = ({ message = 'Easy Health Digital Solutions', progress = 0, onComplete }) => {
  const theme = useTheme();

  useEffect(() => {
    // If progress reaches 100%, call onComplete after a short delay
    if (progress >= 100 && onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, 800); // Give user time to see 100%

      return () => clearTimeout(timer);
    }
  }, [progress, onComplete]);

  return (
    <SplashContainer>
      <LogoContainer>
        <Box
          component="img"
          src={logoImage}
          alt="BookMyOP Logo"
          sx={{
            width: { xs: '280px', sm: '380px', md: '480px' },
            height: { xs: '280px', sm: '380px', md: '480px' },
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
        {/* <Typography 
          variant="h1" 
          sx={{
            fontWeight: 900,
            background: `linear-gradient(45deg, 
              ${theme.palette.primary.main} 30%, 
              ${theme.palette.secondary.main}CC 90%
            )`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '1.5px',
            textAlign: 'center',
            // filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
            textTransform: 'uppercase',
            fontFamily: 'Roboto',
          }}
        >
          Book My Op
        </Typography> */}
        {/* <Typography 
          variant="subtitle1"
          sx={{
            color: theme.palette.text.secondary,
            opacity: 0.9,
            fontWeight: 500,
            textAlign: 'center',
            textShadow: `0 2px 4px ${theme.palette.primary.dark}40`,
          }}
        >
          Your Healthcare Journey Starts Here
        </Typography> */}
      </LogoContainer>
      
      <Box sx={{ position: 'relative', mb: 3 }}>
        <CustomLoader />
      </Box>

      <ProgressContainer>
        <LinearProgress 
          variant="determinate" 
          value={Math.min(progress, 100)} 
        />
        <ProgressText>
          {Math.round(Math.min(progress, 100))}%
        </ProgressText>
      </ProgressContainer>
      
      <LoadingText
        variant="h6"
        sx={{
          color: theme.palette.text.primary,
          opacity: 0.9,
          fontWeight: 500,
          mt: 2,
          textShadow: `0 2px 4px ${theme.palette.primary.dark}40`,
        }}
      >
        {message}
      </LoadingText>
    </SplashContainer>
  );
};

export default SplashScreen; 